import type {
  AdminAppSignalKey,
  AdminAppSignalStage,
  AdminOverviewSummary,
  AdminProjectionFreshness,
  AdminQueryMeta,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  buildAdminAiRuntimeSummaryViewModel,
  type AdminAiRuntimeSummaryViewModel,
} from "../../shared/locale/admin-ai-runtime-summary";
import {
  buildAdminObservabilityAggregateViewModel,
  type AdminObservabilityAggregateViewModel,
} from "../../shared/locale/admin-observability-aggregate-summary";
import {
  buildAdminAccountDeletionSeveritySummary,
} from "../../shared/locale/admin-account-deletion-severity-summary";
import { formatAdminSourceKindLabel } from "../../shared/formatting/format-admin-source-kind";
import {
  formatAdminJobHotspotHeadline,
  formatAdminJobHotspotSummary,
} from "../../shared/locale/admin-job-hotspot-copy";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";

type OverviewRoute = "/admin" | "/admin/users" | "/admin/device-kpis" | "/admin/failed-flows" | "/admin/jobs" | "/admin/releases";

export type OverviewAxisEntry = {
  description: string;
  eyebrow: string;
  hint: string;
  metricLabel: string;
  metricValue: string;
  title: string;
  to: OverviewRoute;
};

export type OverviewSourceStatusViewModel = {
  freshness: AdminProjectionFreshness;
  freshnessLabel: string;
  kindLabel: string;
  source: string;
};

export type OverviewTopErrorViewModel = {
  contextLabel: string;
  countLabel: string;
  drilldownTo: string;
  errorCode: string;
  signalKey: AdminAppSignalKey;
  stage?: AdminAppSignalStage;
  topFeature?: string;
  topUseCase?: string;
};

export type OverviewUseCaseHealthViewModel = {
  detailLabel: string;
  drilldownTo: string;
  failureCountLabel: string;
  feature: string;
  signalKey: AdminAppSignalKey;
  stage?: AdminAppSignalStage;
  successCountLabel: string;
  successRateLabel: string;
  useCase: string;
};

export type OverviewJobHealthViewModel = {
  detailLabel: string;
  drilldownTo: string;
  failedCountLabel: string;
  headline: string;
  isPriorityHotspot: boolean;
  jobType: string;
  priorityLabel: string;
  queuedCountLabel: string;
  succeededCountLabel: string;
};

export type OverviewReleaseHealthViewModel = {
  appVersion?: string;
  appVersionLabel: string;
  detailLabel: string;
  drilldownTo: string;
  failureCountLabel: string;
  release: string;
  requestCountLabel: string;
  topErrorCode?: string;
};

export type OverviewHeadlineViewModel = {
  detail: string;
  drilldownTo?: string;
  label: string;
  value: string;
};

export type OverviewViewModel = {
  accountDeletionSeverityHeadline?: OverviewHeadlineViewModel;
  aiRuntime?: AdminAiRuntimeSummaryViewModel;
  observability?: AdminObservabilityAggregateViewModel;
  axisEntries: OverviewAxisEntry[];
  clientErrorCountLabel: string;
  currentHotspotHeadline: OverviewHeadlineViewModel;
  freshness: AdminProjectionFreshness;
  generatedAtLabel: string;
  jobHotspotHeadline: OverviewHeadlineViewModel;
  jobHealth: OverviewJobHealthViewModel[];
  primaryTriageHint: string;
  releaseHotspotHeadline: OverviewHeadlineViewModel;
  releaseHealth: OverviewReleaseHealthViewModel[];
  requestCountLabel: string;
  requestId: string;
  serverErrorCountLabel: string;
  sourceStatuses: OverviewSourceStatusViewModel[];
  staleSources: string[];
  timeRangeLabel: string;
  topErrorCodes: OverviewTopErrorViewModel[];
  topErrorHeadline: OverviewHeadlineViewModel;
  unavailableSources: string[];
  useCaseHealth: OverviewUseCaseHealthViewModel[];
  weakestUseCaseHeadline: OverviewHeadlineViewModel;
};

function deriveFreshness(meta: AdminQueryMeta): AdminProjectionFreshness {
  const freshnessValues = Object.values(meta.sourceFreshness);

  if (freshnessValues.includes("stale")) {
    return "stale";
  }

  if (freshnessValues.includes("partial") || freshnessValues.includes("unavailable")) {
    return "partial";
  }

  return "fresh";
}

function deriveUnavailableSources(meta: AdminQueryMeta): string[] {
  return Object.entries(meta.sourceFreshness)
    .filter((entry) => entry[1] === "unavailable")
    .map((entry) => entry[0]);
}

function deriveStaleSources(meta: AdminQueryMeta): string[] {
  return Object.entries(meta.sourceFreshness)
    .filter((entry) => entry[1] === "stale" || entry[1] === "partial")
    .map((entry) => entry[0]);
}

function formatSuccessRate(value: number): string {
  return `${Math.round(value * 1000) / 10}%`;
}

function buildSearchParams(
  entries: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(entries)) {
    if (value && value.length > 0) {
      params.set(key, value);
    }
  }

  const queryString = params.toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}

function buildLiveDrilldownRangeParams(
  summary: AdminOverviewSummary,
): Record<string, string> {
  return {
    from: summary.timeRange.from,
  };
}

function formatTimeRangeLabel(summary: AdminOverviewSummary): string {
  return `${formatAdminDate(summary.timeRange.from)} - ${formatAdminDate(summary.timeRange.to)}`;
}

function formatFreshnessLabel(value: AdminProjectionFreshness, locale: AdminDashboardLocale): string {
  if (value === "fresh") {
    return pickAdminDashboardLocaleText(locale, { en: "fresh", ko: "정상" });
  }

  if (value === "stale") {
    return pickAdminDashboardLocaleText(locale, { en: "stale", ko: "지연" });
  }

  if (value === "partial") {
    return pickAdminDashboardLocaleText(locale, { en: "partial", ko: "부분" });
  }

  return pickAdminDashboardLocaleText(locale, { en: "unavailable", ko: "사용 불가" });
}

function buildSourceStatuses(
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale,
): OverviewSourceStatusViewModel[] {
  return Object.entries(meta.sourceFreshness).map(([source, freshness]) => ({
    source,
    freshness,
    freshnessLabel: formatFreshnessLabel(freshness, locale),
    kindLabel: formatAdminSourceKindLabel(meta.sourceKinds?.[source], locale),
  }));
}

function buildTopErrorContext(
  item: AdminOverviewSummary["topErrorCodes"][number],
  locale: AdminDashboardLocale,
): string {
  const parts = [item.topFeature, item.topUseCase, item.signalKey, item.stage].filter(Boolean);
  return parts.length > 0
    ? parts.join(" / ")
    : pickAdminDashboardLocaleText(locale, { en: "failed flow", ko: "실패 흐름" });
}

function buildWeakestUseCaseHealth(
  summary: AdminOverviewSummary,
): AdminOverviewSummary["useCaseHealth"][number] | null {
  if (summary.useCaseHealth.length === 0) {
    return null;
  }

  return [...summary.useCaseHealth].sort((left, right) => {
    if (right.failureCount !== left.failureCount) {
      return right.failureCount - left.failureCount;
    }

    if (left.successRate !== right.successRate) {
      return left.successRate - right.successRate;
    }

    return left.useCase.localeCompare(right.useCase);
  })[0] ?? null;
}

function buildTopReleaseHealth(
  summary: AdminOverviewSummary,
): AdminOverviewSummary["releaseHealth"][number] | null {
  if (summary.releaseHealth.length === 0) {
    return null;
  }

  return [...summary.releaseHealth].sort((left, right) => {
    if (right.failureCount !== left.failureCount) {
      return right.failureCount - left.failureCount;
    }

    return right.requestCount - left.requestCount;
  })[0] ?? null;
}

function buildTopJobHealth(
  summary: AdminOverviewSummary,
): AdminOverviewSummary["jobHealth"][number] | null {
  if (summary.jobHealth.length === 0) {
    return null;
  }

  return [...summary.jobHealth].sort(compareJobHealthAttention)[0] ?? null;
}

function isPriorityDeletionJob(item: AdminOverviewSummary["jobHealth"][number]): boolean {
  return item.jobType === "account_deletion" && (item.failedCount > 0 || item.queuedCount > 0);
}

function compareJobHealthAttention(
  left: AdminOverviewSummary["jobHealth"][number],
  right: AdminOverviewSummary["jobHealth"][number],
): number {
  const leftPriority = isPriorityDeletionJob(left) ? 0 : left.failedCount > 0 ? 1 : left.queuedCount > 0 ? 2 : 3;
  const rightPriority = isPriorityDeletionJob(right) ? 0 : right.failedCount > 0 ? 1 : right.queuedCount > 0 ? 2 : 3;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  if (right.failedCount !== left.failedCount) {
    return right.failedCount - left.failedCount;
  }

  if (right.queuedCount !== left.queuedCount) {
    return right.queuedCount - left.queuedCount;
  }

  if (right.succeededCount !== left.succeededCount) {
    return right.succeededCount - left.succeededCount;
  }

  return left.jobType.localeCompare(right.jobType);
}

function buildJobPriorityLabel(
  item: AdminOverviewSummary["jobHealth"][number],
  locale: AdminDashboardLocale,
): string {
  if (item.jobType === "account_deletion" && item.failedCount > 0) {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion failed", ko: "삭제 실패" });
  }

  if (item.jobType === "account_deletion" && item.queuedCount > 0) {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion backlog", ko: "삭제 대기" });
  }

  if (item.failedCount > 0) {
    return pickAdminDashboardLocaleText(locale, { en: "Failed jobs", ko: "실패 작업" });
  }

  if (item.queuedCount > 0) {
    return pickAdminDashboardLocaleText(locale, { en: "Queued jobs", ko: "대기 작업" });
  }

  return pickAdminDashboardLocaleText(locale, { en: "Stable jobs", ko: "안정 작업" });
}

function buildJobHeadline(
  item: AdminOverviewSummary["jobHealth"][number],
  locale: AdminDashboardLocale,
): string {
  return formatAdminJobHotspotHeadline({
    jobType: item.jobType,
    failedCount: item.failedCount,
    queuedCount: item.queuedCount,
    succeededCount: item.succeededCount,
  }, locale);
}

function buildJobHotspotHeadline(
  item: AdminOverviewSummary["jobHealth"][number] | null,
  sharedRangeParams: Record<string, string>,
  locale: AdminDashboardLocale,
): OverviewHeadlineViewModel {
  if (!item) {
    return {
      label: pickAdminDashboardLocaleText(locale, { en: "Job hotspot", ko: "작업 핫스팟" }),
      value: pickAdminDashboardLocaleText(locale, { en: "Stable", ko: "안정" }),
      detail: pickAdminDashboardLocaleText(locale, {
        en: "No durable job hotspot currently needs operator attention.",
        ko: "현재 운영자 개입이 필요한 지속 작업 핫스팟이 없습니다.",
      }),
      drilldownTo: "/admin/jobs",
    };
  }

  return {
    label: pickAdminDashboardLocaleText(locale, { en: "Job hotspot", ko: "작업 핫스팟" }),
    value: buildJobHeadline(item, locale),
    detail: formatAdminJobHotspotSummary({
      jobType: item.jobType,
      failedCount: item.failedCount,
      queuedCount: item.queuedCount,
      succeededCount: item.succeededCount,
    }, locale),
    drilldownTo: `/admin/jobs${buildSearchParams({
      ...sharedRangeParams,
      jobType: item.jobType,
    })}`,
  };
}

function buildAccountDeletionSeverityHeadline(
  summary: AdminOverviewSummary,
  sharedRangeParams: Record<string, string>,
  locale: AdminDashboardLocale,
): OverviewHeadlineViewModel | undefined {
  const accountDeletion = summary.jobHealth.find((item) => item.jobType === "account_deletion");

  if (!accountDeletion) {
    return undefined;
  }

  const summaryViewModel = buildAdminAccountDeletionSeveritySummary({
    failedCount: accountDeletion.failedCount,
    processingCount: 0,
    queuedCount: accountDeletion.queuedCount,
    succeededCount: accountDeletion.succeededCount,
  }, `/admin/jobs${buildSearchParams({
    ...sharedRangeParams,
    jobType: "account_deletion",
  })}`, locale);

  return {
    label: summaryViewModel.label,
    value: summaryViewModel.headline,
    detail: summaryViewModel.detail,
    drilldownTo: summaryViewModel.drilldownTo,
  };
}

function buildReleaseHotspotHeadline(
  item: AdminOverviewSummary["releaseHealth"][number] | null,
  sharedRangeParams: Record<string, string>,
  locale: AdminDashboardLocale,
): OverviewHeadlineViewModel {
  if (!item) {
    return {
      label: pickAdminDashboardLocaleText(locale, { en: "Release hotspot", ko: "릴리스 핫스팟" }),
      value: pickAdminDashboardLocaleText(locale, { en: "Stable", ko: "안정" }),
      detail: pickAdminDashboardLocaleText(locale, {
        en: "There is no release regression hotspot right now.",
        ko: "현재 릴리스 회귀 핫스팟이 없습니다.",
      }),
      drilldownTo: "/admin/releases",
    };
  }

  return {
    label: pickAdminDashboardLocaleText(locale, { en: "Release hotspot", ko: "릴리스 핫스팟" }),
    value: item.release,
    detail: `${formatAdminCount(item.requestCount)} ${pickAdminDashboardLocaleText(locale, { en: "requests", ko: "요청" })} / ${formatAdminCount(item.failureCount)} ${pickAdminDashboardLocaleText(locale, { en: "failures", ko: "실패" })}${item.topErrorCode ? ` / ${item.topErrorCode}` : ""}`,
    drilldownTo: `/admin/releases${buildSearchParams({
      ...sharedRangeParams,
      release: item.release,
      appVersion: item.appVersion,
    })}`,
  };
}

export function toOverviewViewModel(
  summary: AdminOverviewSummary,
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale = "ko",
): OverviewViewModel {
  const topError = summary.topErrorCodes[0];
  const weakestUseCase = buildWeakestUseCaseHealth(summary);
  const topRelease = buildTopReleaseHealth(summary);
  const topJob = buildTopJobHealth(summary);
  const sharedRangeParams = buildLiveDrilldownRangeParams(summary);

  const topErrorHeadline: OverviewHeadlineViewModel = topError
    ? {
        label: pickAdminDashboardLocaleText(locale, { en: "Top error", ko: "상위 오류" }),
        value: topError.errorCode,
        detail: `${formatAdminCount(topError.count)} ${pickAdminDashboardLocaleText(locale, { en: "repeats in", ko: "반복 /" })} ${buildTopErrorContext(topError, locale)}`,
      }
    : {
        label: pickAdminDashboardLocaleText(locale, { en: "Top error", ko: "상위 오류" }),
        value: pickAdminDashboardLocaleText(locale, { en: "None", ko: "없음" }),
        detail: pickAdminDashboardLocaleText(locale, {
          en: "There is no repeated error code right now.",
          ko: "현재 반복된 오류 코드가 없습니다.",
        }),
      };

  const weakestUseCaseHeadline: OverviewHeadlineViewModel = weakestUseCase
    ? {
        label: pickAdminDashboardLocaleText(locale, { en: "Weakest use case", ko: "가장 약한 유스케이스" }),
        value: weakestUseCase.useCase,
        detail: `${formatAdminCount(weakestUseCase.failureCount)} ${pickAdminDashboardLocaleText(locale, { en: "failures", ko: "실패" })} / ${formatSuccessRate(
          weakestUseCase.successRate,
        )} ${pickAdminDashboardLocaleText(locale, { en: "success", ko: "성공" })}`,
      }
    : {
        label: pickAdminDashboardLocaleText(locale, { en: "Weakest use case", ko: "가장 약한 유스케이스" }),
        value: pickAdminDashboardLocaleText(locale, { en: "Stable", ko: "안정" }),
        detail: pickAdminDashboardLocaleText(locale, {
          en: "There is no health row right now.",
          ko: "현재 건강 상태 행이 없습니다.",
        }),
      };

  const jobHotspotHeadline = buildJobHotspotHeadline(topJob, sharedRangeParams, locale);
  const releaseHotspotHeadline = buildReleaseHotspotHeadline(topRelease, sharedRangeParams, locale);
  const currentHotspotHeadline =
    topJob && isPriorityDeletionJob(topJob)
      ? {
          ...jobHotspotHeadline,
          label: pickAdminDashboardLocaleText(locale, { en: "Current hotspot", ko: "현재 핫스팟" }),
        }
      : {
          ...topErrorHeadline,
          label: pickAdminDashboardLocaleText(locale, { en: "Current hotspot", ko: "현재 핫스팟" }),
          drilldownTo: topError
            ? `/admin/failed-flows${buildSearchParams({
                ...sharedRangeParams,
                errorCode: topError.errorCode,
              })}`
            : undefined,
        };

  const axisEntries: OverviewAxisEntry[] = [
    {
      eyebrow: pickAdminDashboardLocaleText(locale, { en: "Primary axis", ko: "주요 축" }),
      title: pickAdminDashboardLocaleText(locale, { en: "User data lookup", ko: "사용자 데이터 조회" }),
      description: pickAdminDashboardLocaleText(locale, {
        en: "Review user reports, save state, and risk summary in one operator flow.",
        ko: "사용자 신고, 저장 상태, 위험 요약을 한 흐름으로 확인합니다.",
      }),
      metricLabel: weakestUseCase
        ? pickAdminDashboardLocaleText(locale, { en: "Needs user triage", ko: "사용자 점검 필요" })
        : pickAdminDashboardLocaleText(locale, { en: "User triage", ko: "사용자 점검" }),
      metricValue: weakestUseCase ? formatAdminCount(weakestUseCase.failureCount) : pickAdminDashboardLocaleText(locale, { en: "Open", ko: "열기" }),
      hint: weakestUseCase
        ? `${weakestUseCase.useCase} / ${formatSuccessRate(weakestUseCase.successRate)} success`
        : pickAdminDashboardLocaleText(locale, {
            en: "Open latest failure, recent saves, and risk summary.",
            ko: "최근 실패, 최근 저장, 위험 요약으로 이동",
          }),
      to: "/admin/users",
    },
    {
      eyebrow: pickAdminDashboardLocaleText(locale, { en: "Primary axis", ko: "주요 축" }),
      title: pickAdminDashboardLocaleText(locale, { en: "Device KPI explorer", ko: "실기기 지표 탐색기" }),
      description: pickAdminDashboardLocaleText(locale, {
        en: "Inspect real usage, success, and falloff by install, release, and appVersion.",
        ko: "설치본, 릴리스, 앱 버전 기준의 실제 사용량과 성공/이탈 구간을 봅니다.",
      }),
      metricLabel: topRelease
        ? pickAdminDashboardLocaleText(locale, { en: "Top release traffic", ko: "최대 릴리스 트래픽" })
        : pickAdminDashboardLocaleText(locale, { en: "Device KPI", ko: "실기기 지표" }),
      metricValue: topRelease ? formatAdminCount(topRelease.requestCount) : pickAdminDashboardLocaleText(locale, { en: "Open", ko: "열기" }),
      hint: topRelease
        ? `${topRelease.release}${topRelease.appVersion ? ` / ${topRelease.appVersion}` : ""}`
        : pickAdminDashboardLocaleText(locale, {
            en: "Move to release/install funnel and failure drill-down.",
            ko: "릴리스/설치본 단계 흐름과 실패 드릴다운으로 이동",
          }),
      to: "/admin/device-kpis",
    },
    {
      eyebrow: pickAdminDashboardLocaleText(locale, { en: "Primary axis", ko: "주요 축" }),
      title: pickAdminDashboardLocaleText(locale, { en: "Failed flow explorer", ko: "오류 흐름 탐색기" }),
      description: pickAdminDashboardLocaleText(locale, {
        en: "Narrow failure traces by request, correlation, device/install, and release.",
        ko: "요청, 상관관계, 기기/설치본, 릴리스 기준으로 실패 추적을 좁힙니다.",
      }),
      metricLabel: topError
        ? pickAdminDashboardLocaleText(locale, { en: "Top error repeats", ko: "반복 상위 오류" })
        : pickAdminDashboardLocaleText(locale, { en: "Failed flows", ko: "오류 흐름" }),
      metricValue: topError ? formatAdminCount(topError.count) : formatAdminCount(summary.serverErrorCount + summary.clientErrorCount),
      hint: topError
        ? `${topError.errorCode} / ${buildTopErrorContext(topError, locale)}`
        : pickAdminDashboardLocaleText(locale, {
            en: "Move to the server/client error hotspot.",
            ko: "서버/클라이언트 오류 핫스팟으로 이동",
          }),
      to: "/admin/failed-flows",
    },
  ];

  return {
    accountDeletionSeverityHeadline: buildAccountDeletionSeverityHeadline(summary, sharedRangeParams, locale),
    aiRuntime: buildAdminAiRuntimeSummaryViewModel(summary.aiRuntimeOperations, locale, [
      "aiProviderRouteLabel",
      "aiQuotaState",
      "aiServingAccessState",
      "aiBudgetThresholdState",
      "aiProviderLatencyBucket",
      "manualReviewFallbackPressure",
      "aiModelUsageLineage",
      "aiModelUsageSummary",
      "aiModelLimitSummary",
    ]),
    observability: buildAdminObservabilityAggregateViewModel(summary.observabilityAggregate, locale, [
      "businessFlowCompletionRate",
      "failureReasonBucketSummary",
      "releaseErrorSpikeSummary",
      "jobRetryPressure",
      "oldestQueuedAgeBucket",
      "providerFailureDistribution",
    ]),
    requestCountLabel: formatAdminCount(summary.requestCount),
    serverErrorCountLabel: formatAdminCount(summary.serverErrorCount),
    clientErrorCountLabel: formatAdminCount(summary.clientErrorCount),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    freshness: deriveFreshness(meta),
    unavailableSources: deriveUnavailableSources(meta),
    staleSources: deriveStaleSources(meta),
    timeRangeLabel: formatTimeRangeLabel(summary),
    sourceStatuses: buildSourceStatuses(meta, locale),
    currentHotspotHeadline,
    jobHotspotHeadline,
    releaseHotspotHeadline,
    primaryTriageHint: topJob && isPriorityDeletionJob(topJob)
      ? pickAdminDashboardLocaleText(locale, {
          en: "Account deletion failed/backlog is the top priority. Open the deletion row in jobs first, then continue with the same requestId into failed flows and users.",
          ko: "계정 삭제 실패/대기가 현재 최우선입니다. 작업 화면에서 삭제 행을 먼저 열고, 같은 요청 ID는 오류 흐름과 사용자 드릴다운으로 이어가세요.",
        })
      : topError
      ? pickAdminDashboardLocaleText(locale, {
          en: `Start with repeated error ${topError.errorCode} in failed flows, then pivot to KPI and release regression on the install/release axis.`,
          ko: `반복 오류 ${topError.errorCode}부터 오류 흐름으로 좁히고, 설치본/릴리스 축은 KPI와 릴리스 회귀로 이어집니다.`,
        })
      : pickAdminDashboardLocaleText(locale, {
          en: "Start from the primary axis closest to the current operator question: user, install KPI, or failed trace.",
          ko: "먼저 주요 축 진입점에서 사용자, 설치본 지표, 실패 추적 중 현재 문의에 가장 가까운 축으로 내려가세요.",
        }),
    axisEntries,
    topErrorHeadline,
    weakestUseCaseHeadline,
    topErrorCodes: summary.topErrorCodes.map((item) => ({
      errorCode: item.errorCode,
      countLabel: formatAdminCount(item.count),
      signalKey: item.signalKey,
      stage: item.stage,
      topFeature: item.topFeature,
      topUseCase: item.topUseCase,
      contextLabel: buildTopErrorContext(item, locale),
      drilldownTo: `/admin/failed-flows${buildSearchParams({
        ...sharedRangeParams,
        errorCode: item.errorCode,
      })}`,
    })),
    useCaseHealth: summary.useCaseHealth.map((item) => ({
      feature: item.feature,
      useCase: item.useCase,
      signalKey: item.signalKey,
      stage: item.stage,
      successCountLabel: formatAdminCount(item.successCount),
      failureCountLabel: formatAdminCount(item.failureCount),
      successRateLabel: formatSuccessRate(item.successRate),
      detailLabel: [item.signalKey, item.stage].filter(Boolean).join(" / "),
      drilldownTo: `/admin/failed-flows${buildSearchParams({
        ...sharedRangeParams,
        feature: item.feature,
        useCase: item.useCase,
      })}`,
    })),
    jobHealth: [...summary.jobHealth].sort(compareJobHealthAttention).map((item) => ({
      jobType: item.jobType,
      headline: buildJobHeadline(item, locale),
      priorityLabel: buildJobPriorityLabel(item, locale),
      isPriorityHotspot: isPriorityDeletionJob(item),
      queuedCountLabel: formatAdminCount(item.queuedCount),
      failedCountLabel: formatAdminCount(item.failedCount),
      succeededCountLabel: formatAdminCount(item.succeededCount),
      detailLabel: `${item.signalKey} / ${formatAdminJobHotspotSummary({
        jobType: item.jobType,
        failedCount: item.failedCount,
        queuedCount: item.queuedCount,
        succeededCount: item.succeededCount,
      }, locale)}`,
      drilldownTo: `/admin/jobs${buildSearchParams({
        ...sharedRangeParams,
        jobType: item.jobType,
      })}`,
    })),
    releaseHealth: summary.releaseHealth.map((item) => ({
      release: item.release,
      appVersion: item.appVersion,
      appVersionLabel: item.appVersion ?? "-",
      requestCountLabel: formatAdminCount(item.requestCount),
      failureCountLabel: formatAdminCount(item.failureCount),
      topErrorCode: item.topErrorCode,
      detailLabel: item.topErrorCode
        ? pickAdminDashboardLocaleText(locale, {
            en: `top error ${item.topErrorCode}`,
            ko: `상위 오류 ${item.topErrorCode}`,
          })
        : pickAdminDashboardLocaleText(locale, { en: "no top error", ko: "상위 오류 없음" }),
      drilldownTo: `/admin/releases${buildSearchParams({
        ...sharedRangeParams,
        release: item.release,
        appVersion: item.appVersion,
      })}`,
    })),
  };
}
